import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    Divider,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import { EditOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import {
    FiscalConfigurationResponse,
    FiscalEstablishment,
    FiscalProfileForm,
    FiscalProfileResponse,
    FiscalPointOfEmission,
    FiscalSequence,
    OrganizationFiscalSourceResponse,
    ProfileResponse,
    RuntimeEnvironmentResponse
} from '../store/user/types';
import {
    getIdentityOrganizationFiscalSourceAPI,
    getSalesDocumentFiscalConfigurationAPI,
    getSalesDocumentFiscalProfileAPI,
    getSalesDocumentRuntimeEnvironmentAPI,
    postSalesDocumentFiscalEstablishmentAPI,
    postSalesDocumentFiscalPointAPI,
    postSalesDocumentFiscalSequenceAPI,
    postSalesDocumentFiscalProfileAPI,
    putSalesDocumentFiscalEstablishmentAPI,
    putSalesDocumentFiscalPointAPI,
    putSalesDocumentFiscalSequenceAPI,
    putSalesDocumentFiscalProfileAPI
} from '../store/user/restCallUser';
const { Text } = Typography;

interface FiscalConfigurationProps {
    getProfile: () => Promise<ProfileResponse | undefined>;
    getOrganizationManagementCenters?: (organizationId: string) => Promise<any>;
}

const emptyForm: FiscalProfileForm = {
    organizationName: '',
    ruc: '',
    legalName: '',
    tradeName: '',
    fiscalAddress: '',
    email: '',
    phone: '',
    accountingRequired: false,
    taxpayerCategory: undefined,
    isSpecialTaxpayer: false,
    specialTaxpayerNumber: '',
    isWithholdingAgent: false,
    withholdingAgentResolution: '',
    taxRegime: 'GENERAL',
    status: 'ACTIVO'
};

interface CenterOption {
    id: string;
    name: string;
    status?: string;
}

interface FiscalEstablishmentForm {
    msIdentityCenterId: string;
    msIdentityCenterName: string;
    code: string;
    isMatrix: boolean;
    status: string;
}

interface FiscalPointForm {
    code: string;
    description?: string;
    status: string;
}

interface FiscalSequenceForm {
    organizationBillingDocumentTypeId: string;
    lastNumber: number;
    status: string;
}

const normalizeCenters = (source: any): CenterOption[] => {
    const values = Array.isArray(source) ? source : source?.centers || source?.data || [];
    return Array.isArray(values)
        ? values.map((item: any) => ({
            id: String(item.id || item.Id || ''),
            name: String(item.name || item.Name || 'Centro'),
            status: String(item.status || item.Status || 'ACTIVO')
        })).filter((item: CenterOption) => item.id)
        : [];
};

function getProfileOrganization(profile: ProfileResponse | undefined) {
    const contract = profile?.contract || profile?.Contract || {};
    const organizationId = contract.organizationId || contract.OrganizationId || profile?.organizationId;
    const organizationName = contract.organizationName || contract.OrganizationName || profile?.organizationName;
    return {
        organizationId: organizationId ? String(organizationId) : '',
        organizationName: organizationName ? String(organizationName) : ''
    };
}

function FiscalConfiguration({ getProfile, getOrganizationManagementCenters }: FiscalConfigurationProps) {
    const [form] = Form.useForm<FiscalProfileForm>();
    const [establishmentForm] = Form.useForm<FiscalEstablishmentForm>();
    const [pointForm] = Form.useForm<FiscalPointForm>();
    const [sequenceForm] = Form.useForm<FiscalSequenceForm>();
    const [profile, setProfile] = useState<FiscalProfileResponse | null>(null);
    const [configuration, setConfiguration] = useState<FiscalConfigurationResponse | null>(null);
    const [centers, setCenters] = useState<CenterOption[]>([]);
    const [organizationId, setOrganizationId] = useState('');
    const [organizationName, setOrganizationName] = useState('');
    const [runtimeEnvironment, setRuntimeEnvironment] = useState<RuntimeEnvironmentResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isSpecialTaxpayer = Boolean(Form.useWatch('isSpecialTaxpayer', form));
    const isWithholdingAgent = Boolean(Form.useWatch('isWithholdingAgent', form));
    const [establishmentModalOpen, setEstablishmentModalOpen] = useState(false);
    const [pointModalOpen, setPointModalOpen] = useState(false);
    const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
    const [editingEstablishment, setEditingEstablishment] = useState<FiscalEstablishment | null>(null);
    const [editingPoint, setEditingPoint] = useState<FiscalPointOfEmission | null>(null);
    const [editingSequence, setEditingSequence] = useState<FiscalSequence | null>(null);
    const [selectedEstablishmentId, setSelectedEstablishmentId] = useState<string>('');
    const [selectedPointId, setSelectedPointId] = useState<string>('');
    const [savingFiscalConfiguration, setSavingFiscalConfiguration] = useState(false);

    const loadConfiguration = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const runtimeResponse = await getSalesDocumentRuntimeEnvironmentAPI();
            if (runtimeResponse.Error === null
                && runtimeResponse.StatusCode >= 200
                && runtimeResponse.StatusCode < 300) {
                setRuntimeEnvironment((runtimeResponse.Response?.data || runtimeResponse.Response) as RuntimeEnvironmentResponse);
            } else {
                setRuntimeEnvironment(null);
            }

            const identityProfile = await getProfile();
            const organization = getProfileOrganization(identityProfile);

            if (!organization.organizationId) {
                throw new Error('No se pudo identificar la organización emisora de TheBlueCoding.');
            }

            setOrganizationId(organization.organizationId);
            setOrganizationName(organization.organizationName);
            if (getOrganizationManagementCenters) {
                try {
                    const centersResponse = await getOrganizationManagementCenters(organization.organizationId);
                    setCenters(normalizeCenters(centersResponse));
                } catch {
                    setCenters([]);
                }
            }
            const sourceResponse = await getIdentityOrganizationFiscalSourceAPI(organization.organizationId);

            if (sourceResponse.Error !== null || sourceResponse.StatusCode < 200 || sourceResponse.StatusCode >= 300) {
                throw new Error('No se pudieron cargar los datos base de la organización desde Identity.');
            }

            const organizationSource = (sourceResponse.Response?.data || sourceResponse.Response) as OrganizationFiscalSourceResponse;
            const response = await getSalesDocumentFiscalProfileAPI(organization.organizationId);

            if (response.StatusCode === 404) {
                setProfile(null);
                setConfiguration(null);
                form.setFieldsValue({
                    ...emptyForm,
                    organizationName: organizationSource.organizationName || organization.organizationName,
                    fiscalAddress: organizationSource.address || '',
                    email: organizationSource.email || '',
                    phone: organizationSource.phone || ''
                });
                return;
            }

            if (response.Error !== null || response.StatusCode < 200 || response.StatusCode >= 300) {
                throw new Error('No se pudo cargar la configuración fiscal.');
            }

            const fiscalProfile = (response.Response?.data || response.Response) as FiscalProfileResponse;
            setProfile(fiscalProfile);
            setOrganizationName(fiscalProfile.organizationName || organizationSource.organizationName || organization.organizationName);
            form.setFieldsValue({
                organizationName: fiscalProfile.organizationName || organizationSource.organizationName || organization.organizationName,
                ruc: fiscalProfile.ruc || '',
                legalName: fiscalProfile.legalName || '',
                tradeName: fiscalProfile.tradeName || '',
                fiscalAddress: fiscalProfile.fiscalAddress || organizationSource.address || '',
                email: fiscalProfile.email || organizationSource.email || '',
                phone: fiscalProfile.phone || organizationSource.phone || '',
                accountingRequired: Boolean(fiscalProfile.accountingRequired),
                taxpayerCategory: fiscalProfile.taxpayerCategory || undefined,
                isSpecialTaxpayer: Boolean(fiscalProfile.isSpecialTaxpayer),
                specialTaxpayerNumber: fiscalProfile.specialTaxpayerNumber || '',
                isWithholdingAgent: Boolean(fiscalProfile.isWithholdingAgent),
                withholdingAgentResolution: fiscalProfile.withholdingAgentResolution || '',
                taxRegime: fiscalProfile.taxRegime || 'GENERAL',
                status: fiscalProfile.status === 'INACTIVO' ? 'INACTIVO' : 'ACTIVO'
            });

            const configurationResponse = await getSalesDocumentFiscalConfigurationAPI(organization.organizationId);
            if (configurationResponse.Error === null
                && configurationResponse.StatusCode >= 200
                && configurationResponse.StatusCode < 300) {
                setConfiguration((configurationResponse.Response?.data || configurationResponse.Response) as FiscalConfigurationResponse);
            } else {
                setConfiguration(null);
            }
        } catch (loadError: any) {
            setProfile(null);
            setConfiguration(null);
            setError(loadError?.message || 'No se pudo cargar la configuración fiscal.');
        } finally {
            setLoading(false);
        }
    }, [form, getOrganizationManagementCenters, getProfile]);

    useEffect(() => {
        void loadConfiguration();
    }, [loadConfiguration]);

    useEffect(() => {
        const establishments = configuration?.establishments || [];
        if (!establishments.some(item => item.id === selectedEstablishmentId)) {
            setSelectedEstablishmentId(establishments[0]?.id || '');
        }
    }, [configuration, selectedEstablishmentId]);

    useEffect(() => {
        const points = configuration?.pointsOfEmission || [];
        const pointsForEstablishment = points.filter(item => item.organizationFiscalEstablishmentId === selectedEstablishmentId);
        if (!pointsForEstablishment.some(item => item.id === selectedPointId)) {
            setSelectedPointId(pointsForEstablishment[0]?.id || '');
        }
    }, [configuration, selectedEstablishmentId, selectedPointId]);

    const reloadFiscalConfiguration = async () => {
        if (!organizationId) {
            return;
        }

        const response = await getSalesDocumentFiscalConfigurationAPI(organizationId);
        if (response.Error === null && response.StatusCode >= 200 && response.StatusCode < 300) {
            setConfiguration((response.Response?.data || response.Response) as FiscalConfigurationResponse);
        }
    };

    const saveConfiguration = async (values: FiscalProfileForm) => {
        if (!organizationId) {
            setError('No se pudo identificar la organización emisora.');
            return;
        }

        setSaving(true);
        setError(null);
        const payload: FiscalProfileForm = {
            ...values,
            organizationName: values.organizationName.trim(),
            ruc: values.ruc.trim(),
            legalName: values.legalName.trim(),
            tradeName: values.tradeName?.trim() || '',
            fiscalAddress: values.fiscalAddress.trim(),
            email: values.email.trim(),
            phone: values.phone.trim(),
            taxpayerCategory: values.taxpayerCategory?.trim() || undefined,
            isSpecialTaxpayer: Boolean(values.isSpecialTaxpayer),
            specialTaxpayerNumber: values.isSpecialTaxpayer
                ? values.specialTaxpayerNumber?.trim() || ''
                : '',
            isWithholdingAgent: Boolean(values.isWithholdingAgent),
            withholdingAgentResolution: values.isWithholdingAgent
                ? values.withholdingAgentResolution?.trim() || ''
                : ''
        };

        try {
            const response = profile
                ? await putSalesDocumentFiscalProfileAPI(organizationId, payload)
                : await postSalesDocumentFiscalProfileAPI(organizationId, payload);

            if (response.Error !== null || response.StatusCode < 200 || response.StatusCode >= 300) {
                throw new Error('No se pudo guardar la configuración fiscal.');
            }

            message.success(profile ? 'Configuración fiscal actualizada.' : 'Configuración fiscal registrada.');
            await loadConfiguration();
        } catch (saveError: any) {
            setError(saveError?.message || 'No se pudo guardar la configuración fiscal.');
        } finally {
            setSaving(false);
        }
    };

    const saveEstablishment = async (values: FiscalEstablishmentForm) => {
        if (!organizationId) {
            return;
        }

        setSavingFiscalConfiguration(true);
        try {
            const center = centers.find(item => item.id === values.msIdentityCenterId);
            const payload = {
                msIdentityCenterId: values.msIdentityCenterId,
                msIdentityCenterName: center?.name || values.msIdentityCenterName,
                code: values.code.trim(),
                isMatrix: Boolean(values.isMatrix),
                status: values.status
            };
            const response = editingEstablishment
                ? await putSalesDocumentFiscalEstablishmentAPI(organizationId, editingEstablishment.id, payload)
                : await postSalesDocumentFiscalEstablishmentAPI(organizationId, payload);
            if (response.Error !== null || response.StatusCode < 200 || response.StatusCode >= 300) {
                throw new Error('No se pudo guardar el establecimiento fiscal.');
            }
            message.success(editingEstablishment ? 'Establecimiento actualizado.' : 'Establecimiento registrado.');
            setEstablishmentModalOpen(false);
            setEditingEstablishment(null);
            establishmentForm.resetFields();
            await reloadFiscalConfiguration();
        } catch (saveError: any) {
            setError(saveError?.message || 'No se pudo guardar el establecimiento fiscal.');
        } finally {
            setSavingFiscalConfiguration(false);
        }
    };

    const savePoint = async (values: FiscalPointForm) => {
        if (!organizationId || !selectedEstablishmentId) {
            setError('Seleccione un establecimiento fiscal antes de configurar el punto de emisión.');
            return;
        }

        setSavingFiscalConfiguration(true);
        try {
            const payload = {
                code: values.code.trim(),
                description: values.description?.trim() || null,
                status: values.status
            };
            const response = editingPoint
                ? await putSalesDocumentFiscalPointAPI(organizationId, editingPoint.id, payload)
                : await postSalesDocumentFiscalPointAPI(organizationId, selectedEstablishmentId, payload);
            if (response.Error !== null || response.StatusCode < 200 || response.StatusCode >= 300) {
                throw new Error('No se pudo guardar el punto de emisión.');
            }
            message.success(editingPoint ? 'Punto de emisión actualizado.' : 'Punto de emisión registrado.');
            setPointModalOpen(false);
            setEditingPoint(null);
            pointForm.resetFields();
            await reloadFiscalConfiguration();
        } catch (saveError: any) {
            setError(saveError?.message || 'No se pudo guardar el punto de emisión.');
        } finally {
            setSavingFiscalConfiguration(false);
        }
    };

    const saveSequence = async (values: FiscalSequenceForm) => {
        if (!organizationId || !selectedPointId) {
            setError('Seleccione un punto de emisión antes de configurar una secuencia.');
            return;
        }

        setSavingFiscalConfiguration(true);
        try {
            const payload = {
                organizationBillingDocumentTypeId: values.organizationBillingDocumentTypeId,
                lastNumber: Number(values.lastNumber || 0),
                status: values.status
            };
            const response = editingSequence
                ? await putSalesDocumentFiscalSequenceAPI(organizationId, editingSequence.id, payload)
                : await postSalesDocumentFiscalSequenceAPI(organizationId, selectedPointId, payload);
            if (response.Error !== null || response.StatusCode < 200 || response.StatusCode >= 300) {
                throw new Error('No se pudo guardar la secuencia fiscal.');
            }
            message.success(editingSequence ? 'Secuencia actualizada.' : 'Secuencia registrada.');
            setSequenceModalOpen(false);
            setEditingSequence(null);
            sequenceForm.resetFields();
            await reloadFiscalConfiguration();
        } catch (saveError: any) {
            setError(saveError?.message || 'No se pudo guardar la secuencia fiscal.');
        } finally {
            setSavingFiscalConfiguration(false);
        }
    };

    const openEstablishment = (item?: FiscalEstablishment) => {
        setEditingEstablishment(item || null);
        establishmentForm.setFieldsValue({
            msIdentityCenterId: item?.msIdentityCenterId || centers[0]?.id || '',
            msIdentityCenterName: item?.msIdentityCenterName || centers[0]?.name || '',
            code: item?.code || '',
            isMatrix: item?.isMatrix ?? true,
            status: item?.status || 'ACTIVO'
        });
        setEstablishmentModalOpen(true);
    };

    const openPoint = (item?: FiscalPointOfEmission) => {
        setEditingPoint(item || null);
        pointForm.setFieldsValue({
            code: item?.code || '',
            description: item?.description || '',
            status: item?.status || 'ACTIVO'
        });
        setPointModalOpen(true);
    };

    const openSequence = (item?: FiscalSequence) => {
        setEditingSequence(item || null);
        sequenceForm.setFieldsValue({
            organizationBillingDocumentTypeId: item?.organizationBillingDocumentTypeId || configuration?.billingDocumentTypes?.[0]?.id || '',
            lastNumber: item?.lastNumber ?? 0,
            status: item?.status || 'ACTIVO'
        });
        setSequenceModalOpen(true);
    };

    const establishments = configuration?.establishments || [];
    const points = configuration?.pointsOfEmission || [];
    const sequences = configuration?.sequences || [];
    const billingDocumentTypes = configuration?.billingDocumentTypes || [];
    const activeBillingDocumentTypes = billingDocumentTypes.filter(item => item.status === 'ACTIVO');
    const selectedEstablishment = establishments.find(item => item.id === selectedEstablishmentId);
    const selectedPoint = points.find(item => item.id === selectedPointId);
    const establishmentPoints = points.filter(item => item.organizationFiscalEstablishmentId === selectedEstablishmentId);
    const pointSequences = sequences.filter(item => item.fiscalPointOfEmissionId === selectedPointId);

    return (
        <Card
            title="Configuración fiscal"
            extra={(
                <Space>
                    <Text type="secondary">Emisor: {organizationName || 'TheBlueCoding'}</Text>
                    <Button icon={<ReloadOutlined />} onClick={() => void loadConfiguration()} loading={loading}>
                        Actualizar
                    </Button>
                </Space>
            )}
        >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Alert
                    type="info"
                    showIcon
                    message="Esta configuración pertenece a TheBlueCoding"
                    description="Las organizaciones clientes se administran desde su propio portal y no modifican el emisor de esta pantalla."
                />

                {runtimeEnvironment && (
                    <Alert
                        type={runtimeEnvironment.environment === 'PRODUCTION' ? 'success' : 'info'}
                        showIcon
                        message={`Ambiente activo: ${runtimeEnvironment.label}`}
                        description="El ambiente lo determina el despliegue de la aplicación. No se puede cambiar desde esta pantalla."
                    />
                )}

                <Alert
                    type="warning"
                    showIcon
                    message="Verifique los datos tributarios en el SRI"
                    description="Consulte el RUC oficial antes de registrar o actualizar la información fiscal."
                    action={(
                        <Button
                            type="link"
                            href="https://srienlinea.sri.gob.ec/facturacion-internet/consultas/publico/ruc-datos1.jspa"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Consultar RUC en el SRI
                        </Button>
                    )}
                />

                {error && <Alert type="error" showIcon message={error} />}

                {profile && <Tag color={profile.status === 'ACTIVO' ? 'green' : 'default'}>Perfil {profile.status}</Tag>}

                <Form<FiscalProfileForm>
                    form={form}
                    layout="vertical"
                    initialValues={emptyForm}
                    onFinish={saveConfiguration}
                >
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="organizationName" label="Nombre de la organización" rules={[{ required: true, message: 'Ingrese el nombre de la organización.' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="ruc" label="RUC" rules={[{ required: true, len: 13, message: 'El RUC debe tener 13 caracteres.' }]}>
                                <Input maxLength={13} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="legalName" label="Razón social" rules={[{ required: true, message: 'Ingrese la razón social.' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="tradeName" label="Nombre comercial">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item name="fiscalAddress" label="Dirección fiscal" rules={[{ required: true, message: 'Ingrese la dirección fiscal.' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="email" label="Correo" rules={[{ required: true, type: 'email', message: 'Ingrese un correo válido.' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="phone" label="Teléfono" rules={[{ required: true, message: 'Ingrese el teléfono.' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="taxRegime" label="Régimen tributario" rules={[{ required: true, message: 'Seleccione el régimen.' }]}>
                                <Select options={[{ value: 'GENERAL', label: 'General' }, { value: 'RIMPE', label: 'RIMPE' }]} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="taxpayerCategory" label="Categoría tributaria">
                                <Select
                                    allowClear
                                    placeholder="Seleccione si aplica"
                                    options={[
                                        { value: 'EMPRENDEDOR', label: 'Emprendedor' },
                                        { value: 'NEGOCIO_POPULAR', label: 'Negocio popular' }
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="status" label="Estado" rules={[{ required: true }]}>
                                <Select options={[{ value: 'ACTIVO', label: 'Activo' }, { value: 'INACTIVO', label: 'Inactivo' }]} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="isSpecialTaxpayer" valuePropName="checked" style={{ marginTop: 10 }}>
                                <Checkbox>Contribuyente especial</Checkbox>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="isWithholdingAgent" valuePropName="checked" style={{ marginTop: 10 }}>
                                <Checkbox>Agente de retención</Checkbox>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="specialTaxpayerNumber" label="Número de contribuyente especial">
                                <Input disabled={!isSpecialTaxpayer} placeholder="Número o resolución del SRI" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="withholdingAgentResolution" label="Resolución de agente de retención">
                                <Input disabled={!isWithholdingAgent} placeholder="Número de resolución del SRI" />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item name="accountingRequired" valuePropName="checked" style={{ marginTop: 4 }}>
                                <Checkbox>Obligado a llevar contabilidad</Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                        Guardar configuración fiscal
                    </Button>
                </Form>

                {profile && (
                    <>
                        <Divider orientation="left">Establecimientos fiscales</Divider>
                        <Card
                            size="small"
                            title="Establecimientos registrados en el RUC"
                            extra={(
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => openEstablishment()}
                                    disabled={centers.length === 0}
                                >
                                    Nuevo establecimiento
                                </Button>
                            )}
                        >
                            {centers.length === 0 && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message="No hay centros disponibles en Identity"
                                    description="Primero registre un centro para poder relacionarlo con el establecimiento fiscal."
                                />
                            )}
                            <Table<FiscalEstablishment>
                                style={{ marginTop: 16 }}
                                rowKey="id"
                                size="small"
                                pagination={false}
                                dataSource={establishments}
                                locale={{ emptyText: 'Todavía no hay establecimientos fiscales configurados.' }}
                                columns={[
                                    {
                                        title: 'Código',
                                        dataIndex: 'code',
                                        width: 100,
                                        render: (value: string, row: FiscalEstablishment) => (
                                            <Space direction="vertical" size={0}>
                                                <Text strong>{value}</Text>
                                                {row.isMatrix && <Tag color="blue">Matriz</Tag>}
                                            </Space>
                                        )
                                    },
                                    { title: 'Centro', dataIndex: 'msIdentityCenterName' },
                                    { title: 'Estado', dataIndex: 'status', render: (value: string) => <Tag color={value === 'ACTIVO' ? 'green' : 'default'}>{value}</Tag> },
                                    {
                                        title: '',
                                        key: 'actions',
                                        align: 'right' as const,
                                        render: (_value: unknown, row: FiscalEstablishment) => (
                                            <Button icon={<EditOutlined />} onClick={() => openEstablishment(row)}>Editar</Button>
                                        )
                                    }
                                ]}
                            />
                        </Card>

                        <Card
                            size="small"
                            title="Puntos de emisión"
                            extra={(
                                <Space>
                                    <Select
                                        style={{ minWidth: 260 }}
                                        placeholder="Seleccione un establecimiento"
                                        value={selectedEstablishmentId || undefined}
                                        onChange={setSelectedEstablishmentId}
                                        options={establishments.map(item => ({
                                            value: item.id,
                                            label: `${item.code} - ${item.msIdentityCenterName}`
                                        }))}
                                    />
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => openPoint()}
                                        disabled={!selectedEstablishmentId}
                                    >
                                        Nuevo punto
                                    </Button>
                                </Space>
                            )}
                        >
                            {!selectedEstablishment && <Empty description="Seleccione un establecimiento para ver sus puntos de emisión." />}
                            {selectedEstablishment && (
                                <Table<FiscalPointOfEmission>
                                    style={{ marginTop: 16 }}
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    dataSource={establishmentPoints}
                                    locale={{ emptyText: 'No hay puntos de emisión configurados para este establecimiento.' }}
                                    columns={[
                                        { title: 'Código', dataIndex: 'code', width: 120 },
                                        { title: 'Descripción', dataIndex: 'description', render: (value: string | null) => value || 'Sin descripción' },
                                        { title: 'Estado', dataIndex: 'status', render: (value: string) => <Tag color={value === 'ACTIVO' ? 'green' : 'default'}>{value}</Tag> },
                                        {
                                            title: '',
                                            key: 'actions',
                                            align: 'right' as const,
                                            render: (_value: unknown, row: FiscalPointOfEmission) => (
                                                <Button icon={<EditOutlined />} onClick={() => openPoint(row)}>Editar</Button>
                                            )
                                        }
                                    ]}
                                />
                            )}
                        </Card>

                        <Card
                            size="small"
                            title="Secuencias fiscales"
                            extra={(
                                <Space>
                                    <Select
                                        style={{ minWidth: 260 }}
                                        placeholder="Seleccione un punto de emisión"
                                        value={selectedPointId || undefined}
                                        onChange={setSelectedPointId}
                                        options={establishmentPoints.map(item => ({
                                            value: item.id,
                                            label: `${item.code} - ${item.description || 'Punto de emisión'}`
                                        }))}
                                    />
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => openSequence()}
                                         disabled={!selectedPointId || activeBillingDocumentTypes.length === 0}
                                    >
                                        Nueva secuencia
                                    </Button>
                                </Space>
                            )}
                        >
                             {activeBillingDocumentTypes.length === 0 && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    message="No hay tipos de comprobante habilitados"
                                    description="Primero debe cargar y habilitar el catálogo de factura, nota de crédito o nota de débito para la organización."
                                />
                            )}
                            {!selectedPoint && <Empty description="Seleccione un punto de emisión para ver sus secuencias." />}
                            {selectedPoint && (
                                <Table<FiscalSequence>
                                    style={{ marginTop: 16 }}
                                    rowKey="id"
                                    size="small"
                                    pagination={false}
                                    dataSource={pointSequences}
                                    locale={{ emptyText: 'No hay secuencias configuradas para este punto.' }}
                                    columns={[
                                        {
                                            title: 'Comprobante',
                                            render: (_value: unknown, row: FiscalSequence) => (
                                                <Space direction="vertical" size={0}>
                                                    <Text strong>{row.billingDocumentTypeName || row.billingDocumentTypeCode}</Text>
                                                    <Text type="secondary">Código SRI: {row.sriCode || 'Pendiente'}</Text>
                                                </Space>
                                            )
                                        },
                                        { title: 'Último secuencial', dataIndex: 'lastNumber' },
                                        { title: 'Próximo', render: (_value: unknown, row: FiscalSequence) => String(Number(row.lastNumber || 0) + 1).padStart(9, '0') },
                                        { title: 'Estado', dataIndex: 'status', render: (value: string) => <Tag color={value === 'ACTIVO' ? 'green' : 'default'}>{value}</Tag> },
                                        {
                                            title: '',
                                            key: 'actions',
                                            align: 'right' as const,
                                            render: (_value: unknown, row: FiscalSequence) => (
                                                <Button icon={<EditOutlined />} onClick={() => openSequence(row)}>Editar</Button>
                                            )
                                        }
                                    ]}
                                />
                            )}
                        </Card>

                        <Modal
                            open={establishmentModalOpen}
                            title={editingEstablishment ? 'Editar establecimiento fiscal' : 'Nuevo establecimiento fiscal'}
                            footer={null}
                            onCancel={() => { setEstablishmentModalOpen(false); setEditingEstablishment(null); establishmentForm.resetFields(); }}
                            destroyOnHidden
                        >
                            <Form<FiscalEstablishmentForm>
                                form={establishmentForm}
                                layout="vertical"
                                onFinish={saveEstablishment}
                                initialValues={{ isMatrix: true, status: 'ACTIVO' }}
                            >
                                <Form.Item name="msIdentityCenterId" label="Centro" rules={[{ required: true, message: 'Seleccione el centro.' }]}>
                                    <Select
                                        placeholder="Seleccione el centro relacionado"
                                        options={centers.map(item => ({ value: item.id, label: `${item.name} (${item.status || 'ACTIVO'})` }))}
                                        onChange={(value: string) => {
                                            const center = centers.find(item => item.id === value);
                                            establishmentForm.setFieldValue('msIdentityCenterName', center?.name || '');
                                        }}
                                    />
                                </Form.Item>
                                <Form.Item name="msIdentityCenterName" hidden><Input /></Form.Item>
                                <Form.Item name="code" label="Código del establecimiento" rules={[{ required: true, len: 3, pattern: /^\d{3}$/, message: 'Use exactamente 3 dígitos, por ejemplo 001.' }]}>
                                    <Input maxLength={3} placeholder="001" />
                                </Form.Item>
                                <Form.Item name="isMatrix" valuePropName="checked"><Checkbox>Es establecimiento matriz</Checkbox></Form.Item>
                                <Form.Item name="status" label="Estado" rules={[{ required: true }]}>
                                    <Select options={[{ value: 'ACTIVO', label: 'Activo' }, { value: 'INACTIVO', label: 'Inactivo' }]} />
                                </Form.Item>
                                <Button type="primary" htmlType="submit" loading={savingFiscalConfiguration} icon={<SaveOutlined />}>Guardar establecimiento</Button>
                            </Form>
                        </Modal>

                        <Modal
                            open={pointModalOpen}
                            title={editingPoint ? 'Editar punto de emisión' : 'Nuevo punto de emisión'}
                            footer={null}
                            onCancel={() => { setPointModalOpen(false); setEditingPoint(null); pointForm.resetFields(); }}
                            destroyOnHidden
                        >
                            <Form<FiscalPointForm>
                                form={pointForm}
                                layout="vertical"
                                onFinish={savePoint}
                                initialValues={{ status: 'ACTIVO' }}
                            >
                                <Alert type="info" showIcon message={selectedEstablishment ? `Establecimiento ${selectedEstablishment.code} - ${selectedEstablishment.msIdentityCenterName}` : 'Seleccione un establecimiento'} style={{ marginBottom: 16 }} />
                                <Form.Item name="code" label="Código del punto de emisión" rules={[{ required: true, len: 3, pattern: /^\d{3}$/, message: 'Use exactamente 3 dígitos, por ejemplo 001.' }]}>
                                    <Input maxLength={3} placeholder="001" />
                                </Form.Item>
                                <Form.Item name="description" label="Descripción"><Input placeholder="Facturación principal" /></Form.Item>
                                <Form.Item name="status" label="Estado" rules={[{ required: true }]}>
                                    <Select options={[{ value: 'ACTIVO', label: 'Activo' }, { value: 'INACTIVO', label: 'Inactivo' }]} />
                                </Form.Item>
                                <Button type="primary" htmlType="submit" loading={savingFiscalConfiguration} icon={<SaveOutlined />}>Guardar punto de emisión</Button>
                            </Form>
                        </Modal>

                        <Modal
                            open={sequenceModalOpen}
                            title={editingSequence ? 'Editar secuencia fiscal' : 'Nueva secuencia fiscal'}
                            footer={null}
                            onCancel={() => { setSequenceModalOpen(false); setEditingSequence(null); sequenceForm.resetFields(); }}
                            destroyOnHidden
                        >
                            <Form<FiscalSequenceForm>
                                form={sequenceForm}
                                layout="vertical"
                                onFinish={saveSequence}
                                initialValues={{ lastNumber: 0, status: 'ACTIVO' }}
                            >
                                <Alert type="info" showIcon message={selectedPoint ? `Punto de emisión ${selectedPoint.code}` : 'Seleccione un punto de emisión'} style={{ marginBottom: 16 }} />
                                <Form.Item name="organizationBillingDocumentTypeId" label="Tipo de comprobante" rules={[{ required: true, message: 'Seleccione el tipo de comprobante.' }]}>
                                    <Select
                                        placeholder="Seleccione el comprobante"
                                        options={billingDocumentTypes.filter(item => item.status === 'ACTIVO').map(item => ({
                                            value: item.id,
                                            label: `${item.name} (${item.code}) - SRI ${item.sriCode}`
                                        }))}
                                    />
                                </Form.Item>
                                <Form.Item name="lastNumber" label="Último secuencial utilizado" rules={[{ required: true, type: 'number', min: 0, message: 'Ingrese un secuencial válido.' }]}>
                                    <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                                </Form.Item>
                                <Text type="secondary">Para una serie nueva use 0. El sistema reservará el siguiente número de forma atómica.</Text>
                                <Form.Item name="status" label="Estado" style={{ marginTop: 16 }} rules={[{ required: true }]}>
                                    <Select options={[{ value: 'ACTIVO', label: 'Activo' }, { value: 'INACTIVO', label: 'Inactivo' }]} />
                                </Form.Item>
                                <Button type="primary" htmlType="submit" loading={savingFiscalConfiguration} icon={<SaveOutlined />}>Guardar secuencia</Button>
                            </Form>
                        </Modal>
                    </>
                )}
            </Space>
        </Card>
    );
}

export default FiscalConfiguration;
